import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { ApiDailyControlCustomer } from '../../../api/daily_control/ApiDailyControlCustomer';
import {
  DailyControlCustomer,
  Wallet,
} from '../../../api/daily_control/models/DailyControlCustomerModels';

const DailyControlCustomerList = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DailyControlCustomer[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [searchFullText, setSearchFullText] = useState<string>('');
  const [toggleValues, setToggleValues] = useState(() => ['']);
  const [debounceSearchFullText] = useDebounce(searchFullText, 500);
  const [error, setError] = useState({
    open: false,
    message: '',
  });

  let columns = useMemo<GridColDef<DailyControlCustomer>[]>(
    () => [
      { field: 'id', headerName: 'ID', flex: 1, maxWidth: 90 },
      { field: 'odoo_id', headerName: 'Odoo ID', flex: 1, minWidth: 80 },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
      {
        field: 'expiring_in',
        headerName: 'Contract Status',
        flex: 1,
        minWidth: 150,
        renderCell: (params: any) => {
          const value = params.row.expiring_in;
          const expiring = `Expiring in ${value} days`;
          if (value > 0 && value <= 7) {
            return <b>{expiring}</b>;
          } else if (value > 7) {
            return expiring;
          } else {
            return `Expired ${Math.abs(value)} ago`;
          }
        },
      },
      {
        field: 'wallets.on_demand',
        headerName: 'On Demand',
        flex: 1,
        type: 'boolean',
        minWidth: 100,
        valueGetter: (value, row) => (row.wallet ? row.wallet.on_demand : ''),
      },
      {
        field: 'wallets.balance',
        headerName: 'A Credits',
        flex: 1,
        minWidth: 70,
        align: 'center',
        valueGetter: (value, row) =>
          row.wallet?.balance ? row.wallet.balance : null,
        renderCell: (params: any) => {
          const lowCredits = columnLowCredits(params.row.wallet);
          if (lowCredits.length >= 0 && lowCredits.includes('balance')) {
            return <b>{params.row.wallet?.balance}</b>;
          }
          return params.row.wallet?.balance;
        },
      },
      {
        field: 'wallets.solid_balance',
        headerName: 'B Credits',
        flex: 1,
        align: 'center',
        minWidth: 70,
        valueGetter: (value, row) =>
          row.wallet?.solid_balance ? row.wallet.solid_balance : null,
        renderCell: (params: any) => {
          const lowCredits = columnLowCredits(params.row.wallet);
          if (lowCredits.length >= 0 && lowCredits.includes('solid_balance')) {
            return <b>{params.row.wallet?.solid_balance}</b>;
          }
          return params.row.wallet?.solid_balance;
        },
      },
      {
        field: 'wallets.initial_balance',
        headerName: 'Initial A Credits',
        flex: 1,
        minWidth: 110,
        align: 'center',
        valueGetter: (value, row) =>
          row.wallet ? row.wallet.initial_balance : '',
      },
      {
        field: 'wallets.initial_solid_balance',
        headerName: 'Initial B Credits',
        flex: 1,
        minWidth: 110,
        align: 'center',
        valueGetter: (value, row) =>
          row.wallet ? row.wallet.initial_solid_balance : '',
      },
      {
        field: 'start_date',
        headerName: 'Start Date',
        flex: 1,
        minWidth: 100,
        valueGetter: (value, row) => new Date(row.start_date),
        renderCell: (params: any) =>
          new Date(params.row.start_date).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        flex: 1,
        minWidth: 100,
        valueGetter: (value, row) => new Date(row.end_date),
        renderCell: (params: any) =>
          new Date(params.row.end_date).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        field: 'next_renewal_date',
        headerName: 'Renewal Date',
        flex: 1,
        sortable: false,
        minWidth: 100,
        valueGetter: (value, row) =>
          row.next_renewal_date ? new Date(row.next_renewal_date) : '',
        renderCell: (params: any) =>
          params.row.next_renewal_date
            ? new Date(params.row.next_renewal_date).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '',
      },
      {
        field: 'should_be_renewed',
        headerName: 'To Be Renewed',
        flex: 1,
        sortable: false,
        type: 'boolean',
        minWidth: 120,
      },
      {
        field: 'renewal_type',
        headerName: 'Renewal Type',
        flex: 1,
        align: 'center',
        minWidth: 110,
      },
      {
        field: 'screening',
        headerName: 'Screening',
        flex: 1,
        type: 'boolean',
        minWidth: 80,
      },
      {
        field: 'monitoring',
        headerName: 'Monitoring',
        flex: 1,
        type: 'boolean',
        minWidth: 90,
      },
      {
        field: 'batch_screening',
        headerName: 'Batch Screening',
        flex: 1,
        type: 'boolean',
        minWidth: 120,
      },
      {
        field: 'recaps',
        headerName: 'Recaps',
        flex: 1,
        type: 'boolean',
        minWidth: 70,
      },
      {
        field: 'control_center',
        headerName: 'Control Center',
        flex: 1,
        type: 'boolean',
        minWidth: 130,
      },
    ],
    []
  );

  /**
   * Fetch the paginated customers from the API and set the rows state
   */
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { total, customers } = await ApiDailyControlCustomer.index({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        search: debounceSearchFullText,
        expiring: toggleValues.some((value) => value.includes('expiring')),
        low_credits: toggleValues.some((value) =>
          value.includes('low_credits')
        ),
        sort_field: sortModel[0]?.field ?? 'id',
        sort_direction: sortModel[0]?.sort ?? 'asc',
      });
      setRowCount(total);
      setRows(customers);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching the customers: 
        ${error.response?.data.error ?? 'Error fetching the customers'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debounceSearchFullText,
    toggleValues,
    sortModel,
  ]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  const handleToggleValueChange = (newFormats: string[]) => {
    setToggleValues(newFormats);
  };

  const columnLowCredits = (wallet: Wallet | undefined): string[] => {
    const lowCredits: string[] = [];
    if (wallet) {
      const fluid_balance = wallet.balance;
      const solid_balance = wallet.solid_balance;
      if (solid_balance < 0.2 * wallet.initial_solid_balance) {
        lowCredits.push('solid_balance');
      }
      if (!wallet.on_demand && fluid_balance < 0.2 * wallet.initial_balance) {
        lowCredits.push('balance');
      }
    }
    return lowCredits;
  };

  const getClassName = (customer: DailyControlCustomer): string => {
    if (customer.expiring_in < 0) {
      return 'expired-contract';
    } else if (
      (customer.expiring_in >= 0 && customer.expiring_in <= 7) ||
      columnLowCredits(customer.wallet).length > 0
    ) {
      return 'expiring-contract';
    }
    return '';
  };

  return (
    <Container>
      <Card sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
        <Stack
          spacing={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchFullText}
              sx={{
                backgroundColor: 'white',
                width: { xs: '100%', sm: 'calc(100% - 200px)' },
              }}
              onChange={(e) => setSearchFullText(e.target.value)}
            />
            <ToggleButtonGroup
              size="small"
              sx={{ flexWrap: 'wrap' }}
              value={toggleValues}
              onChange={(e, value) => handleToggleValueChange(value)}
            >
              <ToggleButton value="expiring">
                {t('hexa_api.filters.expiring')}
              </ToggleButton>
              <ToggleButton value="low_credits">
                {t('hexa_api.filters.low_credits')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <div style={{ display: 'block', overflow: 'auto' }}>
            <DataGridPremium
              getRowId={(row) => row.id}
              getRowClassName={(params) => getClassName(params.row)}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              loading={loading}
              disableColumnResize={false}
              scrollbarSize={20}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={rowCount}
              paginationMode="server"
              pagination={true}
              pageSizeOptions={[10, 25, 50, 100]}
              autoHeight={true}
              rows={rows}
              columns={columns}
            />
          </div>
        </Stack>
        <Snackbar
          open={error.open}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="error">
            {error.message}
          </Alert>
        </Snackbar>
      </Card>
    </Container>
  );
};

export default DailyControlCustomerList;
